/**
 * Module catalog (3D-canonical).
 *
 * Every module declares its path topology (Straight / Curve / Free), its
 * arc length, and — for curves — the signed end-of-module yaw delta in
 * degrees. Centerline radius for analytic curves is derived from
 * `R = L / |θ_rad|`.
 *
 * Hygienic ATH variants share path math with their AT pendants — only the
 * GLB mesh differs.
 *
 * This file is hand-curated. The geometry module imports from here directly
 * (no JSON fetch) so the library has zero runtime data deps.
 */

import type { ModuleType3D } from '../types.js';

export type PathType = 'Straight' | 'Curve' | 'Free';

export interface ModuleCatalogEntry {
  moduleType: ModuleType3D;
  /** Arc length along the centerline, in mm. */
  moduleLengthMm: number;
  pathType: PathType;
  /**
   * Encoder-side end-of-module yaw, in degrees, in the module-local 3D frame.
   * Sign convention: positive endAngle ⇒ Y-flip math curves toward
   * −Y (= right of path). 0 for Straight. 180 for AT2050_*_180 free path.
   */
  endAngleDeg: number;
  /**
   * Centerline radius for analytic curves, in mm. `null` for Straight + Free.
   * Computed as R = L / |θ_rad|; equals 636.6198 for ±22.5° and 318.3099 for
   * ±45° curves at L=250.
   */
  centerlineRadiusMm: number | null;
  /**
   * Free-path point cloud reference. Only set for `pathType==='Free'`.
   * The string identifies the cloud by name; the actual data lives in
   * geometry/PointClouds.ts.
   */
  freePathId?: string;
  /** True if STP is not delivered yet. */
  prepared?: boolean;
  /** True for Einspeisemodule (power feed-in variants). */
  isInfeed?: boolean;
}

const R_22_5 = 250 / (Math.PI / 8); // = 636.61977236...
const R_45 = 250 / (Math.PI / 4); // = 318.30988618...

const STRAIGHT = (
  moduleType: ModuleType3D,
  moduleLengthMm: number,
): ModuleCatalogEntry => ({
  moduleType,
  moduleLengthMm,
  pathType: 'Straight',
  endAngleDeg: 0,
  centerlineRadiusMm: null,
});

const CURVE = (
  moduleType: ModuleType3D,
  endAngleDeg: number,
  moduleLengthMm = 250,
): ModuleCatalogEntry => ({
  moduleType,
  moduleLengthMm,
  pathType: 'Curve',
  endAngleDeg,
  centerlineRadiusMm: moduleLengthMm / Math.abs((endAngleDeg * Math.PI) / 180),
});

const FREE_180 = (
  moduleType: ModuleType3D,
  freePathId: string,
): ModuleCatalogEntry => ({
  moduleType,
  moduleLengthMm: 500,
  pathType: 'Free',
  endAngleDeg: 180,
  centerlineRadiusMm: null,
  freePathId,
});

export const MODULE_CATALOG: Readonly<Record<ModuleType3D, ModuleCatalogEntry>> =
  Object.freeze({
    // Standard AT — Straights
    AT2000_0250: STRAIGHT('AT2000_0250', 250),
    AT2001_0250: { ...STRAIGHT('AT2001_0250', 250), isInfeed: true },
    AT2002_0250: { ...STRAIGHT('AT2002_0250', 250), isInfeed: true },
    AT2002_0249: STRAIGHT('AT2002_0249', 249),
    AT2002_0249_ZX2002_0001: STRAIGHT('AT2002_0249_ZX2002_0001', 249),
    AT2000_0233: STRAIGHT('AT2000_0233', 233),
    AT2000_0249: STRAIGHT('AT2000_0249', 249),
    AT2100_0250: STRAIGHT('AT2100_0250', 250),
    AT2102_0250: { ...STRAIGHT('AT2102_0250', 250), isInfeed: true },

    // Standard AT — Curves
    AT2020_0250: CURVE('AT2020_0250', +22.5),
    AT2021_0250: { ...CURVE('AT2021_0250', +22.5), isInfeed: true },
    AT2025_0250: CURVE('AT2025_0250', -22.5),
    AT2026_0250: { ...CURVE('AT2026_0250', -22.5), isInfeed: true },
    AT2040_0250: CURVE('AT2040_0250', +45),
    AT2041_0250: { ...CURVE('AT2041_0250', +45), isInfeed: true },
    AT2042_0250: { ...CURVE('AT2042_0250', +45), prepared: true },
    AT2140_0250: { ...CURVE('AT2140_0250', +45), prepared: true },

    // Standard AT — Free path (180° clothoid pair)
    AT2050_0500: {
      // Half-clothoid; legacy 2D. In 3D this MUST be merged with AT2050_0501
      // by normalizeXtsConfig before path math runs.
      moduleType: 'AT2050_0500',
      moduleLengthMm: 250,
      pathType: 'Free',
      endAngleDeg: 90,
      centerlineRadiusMm: null,
      freePathId: 'AT2050_0500_HALF',
    },
    AT2050_0501: {
      moduleType: 'AT2050_0501',
      moduleLengthMm: 250,
      pathType: 'Free',
      endAngleDeg: 90,
      centerlineRadiusMm: null,
      freePathId: 'AT2050_0501_HALF',
    },
    AT2050_0500_180: FREE_180('AT2050_0500_180', 'AT2050_180_COMBINED'),

    // Eco
    AT2200_0500: STRAIGHT('AT2200_0500', 500),
    AT2202_0500: { ...STRAIGHT('AT2202_0500', 500), isInfeed: true },

    // Hygienic ATH — Straights
    ATH2000_0250: STRAIGHT('ATH2000_0250', 250),
    ATH2001_0250: { ...STRAIGHT('ATH2001_0250', 250), isInfeed: true },
    ATH2002_0250: { ...STRAIGHT('ATH2002_0250', 250), isInfeed: true },

    // Hygienic ATH — Curves (path math identical to AT pendants)
    ATH2020_0250: CURVE('ATH2020_0250', +22.5),
    ATH2040_0250: CURVE('ATH2040_0250', +45),
    ATH2041_0250: { ...CURVE('ATH2041_0250', +45), isInfeed: true },
    ATH2042_0250: CURVE('ATH2042_0250', +45),

    // Hygienic ATH — Free path
    ATH2050_0500: {
      moduleType: 'ATH2050_0500',
      moduleLengthMm: 250,
      pathType: 'Free',
      endAngleDeg: 90,
      centerlineRadiusMm: null,
      freePathId: 'AT2050_0500_HALF',
    },
    ATH2050_0501: {
      moduleType: 'ATH2050_0501',
      moduleLengthMm: 250,
      pathType: 'Free',
      endAngleDeg: 90,
      centerlineRadiusMm: null,
      freePathId: 'AT2050_0501_HALF',
    },
    ATH2050_0500_180: FREE_180('ATH2050_0500_180', 'AT2050_180_COMBINED'),
  });

/**
 * Beckhoff XTS infeed module types (Einspeisemodule). Convenience constant
 * for use with `moduleHighlights` on `<XtsViewer3D>`.
 *
 * Derived from the catalog (`isInfeed` flag) so it never drifts out of sync —
 * mark a new module with `isInfeed: true` in `MODULE_CATALOG` and it is
 * automatically included here.
 */
export const INFEED_MODULE_TYPES: readonly ModuleType3D[] = Object.freeze(
  (Object.values(MODULE_CATALOG) as ModuleCatalogEntry[])
    .filter((e) => e.isInfeed)
    .map((e) => e.moduleType),
);

/** Lookup with type-narrowing. Returns undefined for unknown types. */
export function getModuleEntry(
  moduleType: ModuleType3D,
): ModuleCatalogEntry | undefined {
  return MODULE_CATALOG[moduleType];
}

/** Type-guard for safe lookups from untyped (e.g. JSON) sources. */
export function isKnownModuleType(value: string): value is ModuleType3D {
  return Object.prototype.hasOwnProperty.call(MODULE_CATALOG, value);
}

export { R_22_5, R_45 };
